/**
 * POs whose lines sit on more than one third-party tab.
 *
 * Each tab shows only the cost types it owns, and JDE codes every PO line to
 * its own account. A PO with lines on two accounts therefore shows part of
 * itself on each tab, and the part you are looking at reads as incomplete:
 * Juan Salinas's PO 90031865 on 12426032 (10/16/2026), lines 1–4 coded 54200
 * External Subcontracts (Subs tab), lines 5–6 added as 52400 Repairs (Other
 * tab), reported as "not updating in Fusion". The PO row says "4/6 lines" and
 * chips the lines that live elsewhere.
 */
import { poKey, lineKey } from './reversedPos';

// Mirrors the tDescriptions keys in materials.js, rentals.js, subcontractors.js
// and other.js (a test keeps them in step).
export const TAB_COST_TYPES = {
    Materials: ['53100', '53200', '53210', '53300', '53310', '53400', '53500'],
    Rentals: ['52200'],
    Subcontractors: ['54100', '54200', '54300'],
    Other: [
        '51800', '52300', '52400',
        '55100', '55200', '55300', '55400', '55900',
        '56100', '56200', '56300', '56400', '56500', '56600', '56700', '56800',
        '57100', '57200', '57300', '57900',
    ],
};

// The names the side menu shows.
export const TAB_LABELS = { Materials: 'Materials', Rentals: 'Rentals', Subcontractors: 'Subs', Other: 'Other' };

const TAB_OF = {};
Object.entries(TAB_COST_TYPES).forEach(([tab, cts]) => cts.forEach((ct) => { TAB_OF[ct] = tab; }));

const ctKey = (ct) => String(ct ?? '').trim();

/** The tab a cost type shows on, or '' when no third-party tab carries it. */
export const tabOfCostType = (ct) => TAB_OF[ctKey(ct)] || '';

// A line's bucket: its tab, or the bare cost type when no tab carries it, so
// a line coded to (say) 52100 still counts as "not here".
const bucketOf = (ct) => tabOfCostType(ct) || (ctKey(ct) ? 'ct:' + ctKey(ct) : '');

const num = (v) => {
    const n = typeof v === 'number' ? v : parseFloat(String(v ?? '').replace(/[$,]/g, ''));
    return Number.isFinite(n) ? n : 0;
};

/**
 * Find the job's POs whose lines land on more than one tab.
 *
 * @param {Array} costRows – cost history (PO_Number, Line_ID, Cost_Type_Code, Job_Cost, Remark_Description)
 * @param {Array} ocRows   – open-PO lines (PO_Number, PO_Line_Number, Cost_Type_Code, Cost_Type_Description,
 *                           PO_Line_Description, PO_Ordered_Amount)
 * @returns {Map} poKey → { po, vendor, lines: Map(lineKey → { line, buckets: Set, costTypes: Set,
 *          costTypeDescription, description, amount }) }
 *          amount is the ordered amount from the PO, else the line's posted cost.
 */
export const findSplitPos = (costRows, ocRows) => {
    const byPo = new Map();
    const lineOf = (po, vendor, line) => {
        let e = byPo.get(po);
        if (!e) { e = { po, vendor: vendor || '', lines: new Map() }; byPo.set(po, e); }
        if (!e.vendor && vendor) e.vendor = vendor;
        let ln = e.lines.get(line);
        if (!ln) {
            ln = { line, buckets: new Set(), costTypes: new Set(), costTypeDescription: '', description: '', ordered: null, cost: 0 };
            e.lines.set(line, ln);
        }
        return ln;
    };
    const note = (ln, ct) => {
        const b = bucketOf(ct);
        if (!b) return;
        ln.buckets.add(b);
        ln.costTypes.add(ctKey(ct));
    };

    (ocRows || []).forEach((r) => {
        const po = poKey(r?.PO_Number);
        const line = lineKey(r?.PO_Line_Number);
        if (!po || !line) return;
        const ln = lineOf(po, r.Supplier_AB_Name || r.Supplier_Name, line);
        note(ln, r.Cost_Type_Code);
        if (!ln.description && r.PO_Line_Description) ln.description = String(r.PO_Line_Description).trim();
        if (!ln.costTypeDescription && r.Cost_Type_Description) ln.costTypeDescription = String(r.Cost_Type_Description).trim();
        if (r.PO_Ordered_Amount != null) ln.ordered = (ln.ordered || 0) + num(r.PO_Ordered_Amount);
    });
    (costRows || []).forEach((r) => {
        const po = poKey(r?.PO_Number);
        const line = lineKey(r?.Line_ID);
        if (!po || !line) return;
        const ln = lineOf(po, r.Supplier_Name, line);
        note(ln, r.Cost_Type_Code);
        if (!ln.description && r.Remark_Description) ln.description = String(r.Remark_Description).trim();
        ln.cost += num(r.Job_Cost);
    });

    const split = new Map();
    byPo.forEach((e, po) => {
        const buckets = new Set();
        e.lines.forEach((ln) => ln.buckets.forEach((b) => buckets.add(b)));
        if (buckets.size < 2) return;
        const lines = new Map();
        e.lines.forEach((ln, key) => {
            if (!ln.buckets.size) return;
            lines.set(key, {
                line: ln.line,
                buckets: ln.buckets,
                costTypes: ln.costTypes,
                costTypeDescription: ln.costTypeDescription,
                description: ln.description,
                amount: ln.ordered != null ? ln.ordered : Math.round(ln.cost * 100) / 100,
            });
        });
        split.set(po, { po, vendor: e.vendor, lines });
    });
    return split;
};

const byLine = (a, b) => parseFloat(a.line) - parseFloat(b.line);

/**
 * How a split PO reads from one tab.
 *
 * @param {Object} entry – a findSplitPos entry
 * @param {string} tab   – 'Materials' | 'Rentals' | 'Subcontractors' | 'Other' | 'All Third Party'
 * @returns {null|Object} null when every line shows on this tab; else
 *          { po, here, total, elsewhere: [{ tab, label, lines: [line entries] }] }
 *          tab is '' for a cost type no third-party tab carries.
 */
export const splitForTab = (entry, tab) => {
    if (!entry?.lines?.size) return null;
    const isHere = tab === 'All Third Party'
        ? (b) => !b.startsWith('ct:')
        : (b) => b === tab;
    let here = 0;
    const away = new Map();
    entry.lines.forEach((ln) => {
        let seenHere = false;
        ln.buckets.forEach((b) => {
            if (isHere(b)) { seenHere = true; return; }
            if (!away.has(b)) away.set(b, []);
            away.get(b).push(ln);
        });
        if (seenHere) here += 1;
    });
    if (!away.size || !here) return null;
    const elsewhere = [...away.entries()]
        .map(([b, lines]) => {
            const t = b.startsWith('ct:') ? '' : b;
            return { tab: t, label: t ? TAB_LABELS[t] : b.slice(3), lines: lines.sort(byLine) };
        })
        .sort((a, b) => (a.tab ? 0 : 1) - (b.tab ? 0 : 1) || byLine(a.lines[0], b.lines[0]));
    return { po: entry.po, here, total: entry.lines.size, elsewhere };
};
